/**
 * Draft a proposal.
 *
 * The whole document in one form — header, terms and lines — because a draft is
 * written in one sitting and a wizard would make somebody navigate to add a
 * line. Everything here is editable until it is sent, after which the detail
 * page offers a revision instead.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Alert,
  ApiError,
  Button,
  Input,
  Modal,
  RecordSelect,
  Select,
  Textarea,
  api,
  toast,
  todayISO,
} from '@connect/web-shared';

import { ProposalLinesEditor, type DraftLine } from './proposal-lines-editor';

const schema = z.object({
  title: z.string().trim().min(1, 'Give the proposal a title.').max(255),
  currency: z.string().trim().length(3, 'Use the three-letter currency code.'),
  tax_rate: z
    .string()
    .trim()
    .regex(/^\d{1,2}(\.\d{1,2})?$/, 'A percentage, to two decimals.'),
  valid_until: z.string().optional(),
  summary: z.string().trim().optional(),
  terms: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'].map((code) => ({
  value: code,
  label: code,
}));

/** A month out. The usual validity, and a date somebody can change. */
function defaultValidity(): string {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  // `todayISO`, not `toISOString`: the month is stepped in LOCAL time, so
  // serialising the result in UTC can hand back the day before the one that
  // was just computed.
  return todayISO(date);
}

const DEFAULTS = (): FormValues => ({
  title: '',
  currency: 'INR',
  tax_rate: '18.00',
  valid_until: defaultValidity(),
  summary: '',
  terms: '',
});

export function NewProposalDialog({
  open,
  onClose,
  onCreated,
  clientPublicId,
  clientName,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (publicId: string) => void;
  clientPublicId?: string;
  clientName?: string;
}) {
  const queryClient = useQueryClient();
  const [client, setClient] = useState<string | null>(clientPublicId ?? null);
  const [lines, setLines] = useState<DraftLine[]>([]);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS() });
  const currency = form.watch('currency');
  const taxRate = form.watch('tax_rate');

  useEffect(() => {
    if (!open) return;
    form.reset(DEFAULTS());
    setClient(clientPublicId ?? null);
    setLines([]);
  }, [open, form, clientPublicId]);

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ public_id: string; code: string }>(
        `/crm/proposals/for-client/${client}`,
        {
          title: values.title,
          currency: values.currency,
          tax_rate: values.tax_rate,
          valid_until: values.valid_until || null,
          summary: values.summary?.trim() || null,
          terms: values.terms?.trim() || null,
          lines: lines
            // A blank row somebody added and did not fill is not a line.
            .filter((line) => line.description.trim() && line.unit_rate.trim())
            .map((line) => ({
              description: line.description.trim(),
              quantity: line.quantity,
              unit_rate: line.unit_rate,
              taxable: line.taxable,
            })),
        },
      ),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['crm', 'proposals'] });
      toast.success('Draft created', record.code);
      onCreated?.(record.public_id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS()) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => {
    if (client === null) {
      form.setError('root', { message: 'Choose the client this is for.' });
      return;
    }
    void form.handleSubmit((values) => create.mutateAsync(values))();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New proposal"
      description="A draft. Nothing is offered until you send it."
      size="xl"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={create.isPending} onClick={submit}>
            Create draft
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        <RecordSelect
          label="Client"
          required
          endpoint="/crm/clients"
          value={client}
          valueLabel={clientName}
          onChange={(next) => setClient(next)}
          toOption={(row) => ({
            value: String(row.public_id),
            label: String(row.name),
            detail: String(row.code ?? ''),
          })}
          placeholder="Search clients…"
        />

        <Input
          label="Title"
          required
          placeholder="Warehouse management rollout"
          error={form.formState.errors.title?.message}
          {...form.register('title')}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Currency"
            options={CURRENCIES}
            error={form.formState.errors.currency?.message}
            {...form.register('currency')}
          />
          <Input
            label="Tax rate (%)"
            inputMode="decimal"
            error={form.formState.errors.tax_rate?.message}
            {...form.register('tax_rate')}
          />
          <Input
            label="Valid until"
            type="date"
            hint="After this it shows as lapsed."
            error={form.formState.errors.valid_until?.message}
            {...form.register('valid_until')}
          />
        </div>

        <ProposalLinesEditor
          value={lines}
          onChange={setLines}
          currency={currency}
          taxRate={taxRate}
        />

        <Textarea
          label="Summary"
          rows={3}
          hint="What is being proposed, in a paragraph. Carried onto the project if this is accepted."
          error={form.formState.errors.summary?.message}
          {...form.register('summary')}
        />
        <Textarea
          label="Terms"
          rows={3}
          hint="Payment terms, assumptions, what is out of scope."
          error={form.formState.errors.terms?.message}
          {...form.register('terms')}
        />
      </form>
    </Modal>
  );
}
